import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import * as XLSX from "xlsx";
import { Sun, Moon, Plus, Pencil, Trash2, X, AlertTriangle, FileSpreadsheet } from "lucide-react";
import {
  getNovasVendasOrcamento, getResumoNovasVendasOrcamento,
  postNovasVendasOrcamento, putNovasVendasOrcamento, deleteNovasVendasOrcamento,
} from "../../api";
import SideBarNavGestaoOrcamentaria from "../../componets/SideBarNavGestaoOrcamentaria";
import { KpiRow } from "../../components/dashboard/KpiCard";
import { MESES_ABREV, formatCurrency } from "../../utils/orcamento";

// Paleta padrão da plataforma (mesma usada nas demais telas de Gestão Orçamentária).
const palette = (isLight) => ({
  bg: isLight ? "#F8F7FC" : "#0F0A1E",
  card: isLight ? "#FFFFFF" : "#1A1030",
  border: isLight ? "#E5E0F0" : "#2E2050",
  text: isLight ? "#1A1030" : "#EDE9FA",
  muted: isLight ? "#7C6FA0" : "#9585C0",
  brand: "#7C3AED",
  bgActive: isLight ? "#F1E9FE" : "#241A3D",
  success: "#10B981",
  danger: "#EF4444",
});

const ANO_ATUAL = new Date().getFullYear();
const OPCOES_ANO = [ANO_ATUAL - 1, ANO_ATUAL, ANO_ATUAL + 1, ANO_ATUAL + 2];

const FORM_VAZIO = {
  codigo: "",
  setor: "",
  nome_projeto: "",
  valor_contrato_2025: "0",
  saldo_novos_aditivos: "0",
  saldo_previsao_2026: "0",
  meses: Array(12).fill("0"),
};

function totalProjeto(projeto) {
  let total = 0;
  for (let mes = 1; mes <= 12; mes++) total += Number(projeto.meses?.[mes] || 0);
  return total;
}

export default function NovasVendasOrcamento() {
  const [isLight, setIsLight] = useState(true);
  const C = palette(isLight);
  const navigate = useNavigate();

  const [ano, setAno] = useState(ANO_ATUAL);

  const [projetos, setProjetos] = useState([]);
  const [resumo, setResumo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState("");

  const [modalAberto, setModalAberto] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(FORM_VAZIO);
  const [erroForm, setErroForm] = useState("");
  const [salvando, setSalvando] = useState(false);

  async function carregarTudo() {
    setLoading(true);
    setErro("");
    try {
      const [lista, resumoDados] = await Promise.all([
        getNovasVendasOrcamento(ano),
        getResumoNovasVendasOrcamento(ano),
      ]);
      setProjetos(lista || []);
      setResumo(resumoDados || null);
    } catch (e) {
      setErro(e.message);
    } finally {
      setLoading(false);
    }
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { carregarTudo(); }, [ano]);

  const projetosOrdenados = useMemo(
    () => [...projetos].sort((a, b) => a.codigo - b.codigo),
    [projetos]
  );

  const totaisPorMes = useMemo(() => {
    const totais = Array(12).fill(0);
    projetosOrdenados.forEach((p) => {
      for (let mes = 1; mes <= 12; mes++) totais[mes - 1] += Number(p.meses?.[mes] || 0);
    });
    return totais;
  }, [projetosOrdenados]);
  const totalGeral = totaisPorMes.reduce((acc, v) => acc + v, 0);

  // ── Exportar para Excel (backup/consulta caso o BD apresente problema) ────
  function exportarParaExcel() {
    const linhas = projetosOrdenados.map((p) => {
      const linha = {
        "Código": p.codigo,
        "Setor": p.setor || "",
        "Nome do Projeto": p.nome_projeto,
        "Valor do Contrato em 31.12.2025": p.valor_contrato_2025,
        "Saldo Novos/Aditivos": p.saldo_novos_aditivos,
        "Saldo Total": p.saldo_total,
        "Saldo Previsão 31.12.2026": p.saldo_previsao_2026,
      };
      MESES_ABREV.forEach((m, i) => { linha[m] = Number(p.meses?.[i + 1] || 0); });
      linha["Total"] = totalProjeto(p);
      return linha;
    });
    linhas.push({
      "Código": "", "Setor": "", "Nome do Projeto": "Total Geral", "Valor do Contrato em 31.12.2025": "",
      "Saldo Novos/Aditivos": "", "Saldo Total": "", "Saldo Previsão 31.12.2026": "",
      ...Object.fromEntries(MESES_ABREV.map((m, i) => [m, totaisPorMes[i]])),
      "Total": totalGeral,
    });

    const worksheet = XLSX.utils.json_to_sheet(linhas);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Novas Vendas".slice(0, 31));
    const nomeArquivo = `Novas_Vendas_${ano}`.replace(/[^\w]+/g, "_");
    XLSX.writeFile(workbook, `${nomeArquivo}.xlsx`);
  }

  const kpiItems = [
    { label: "Total Contratado", value: loading || !resumo ? "—" : formatCurrency(resumo.total_contratado), color: C.text },
    { label: "Saldo Previsão 31.12", value: loading || !resumo ? "—" : formatCurrency(resumo.saldo_previsao_total), color: C.brand },
    { label: "Total Distribuído no Ano", value: loading || !resumo ? "—" : formatCurrency(resumo.total_ano), color: C.success },
    { label: "Total de Projetos", value: loading || !resumo ? "—" : String(resumo.total_projetos), color: C.text },
  ];

  // ── Modal: abrir / salvar / excluir ─────────────────────────────────────
  function abrirModal(projeto = null) {
    setErroForm("");
    if (projeto) {
      setEditando(projeto);
      setForm({
        codigo: String(projeto.codigo),
        setor: projeto.setor || "",
        nome_projeto: projeto.nome_projeto,
        valor_contrato_2025: String(projeto.valor_contrato_2025),
        saldo_novos_aditivos: String(projeto.saldo_novos_aditivos),
        saldo_previsao_2026: String(projeto.saldo_previsao_2026),
        meses: Array.from({ length: 12 }, (_, i) => String(projeto.meses?.[i + 1] ?? 0)),
      });
    } else {
      setEditando(null);
      setForm({ ...FORM_VAZIO, meses: Array(12).fill("0") });
    }
    setModalAberto(true);
  }

  async function salvarProjeto() {
    const codigo = parseInt(form.codigo, 10);
    const valorContrato2025 = parseFloat(form.valor_contrato_2025) || 0;
    const saldoNovosAditivos = parseFloat(form.saldo_novos_aditivos) || 0;
    const saldoPrevisao2026 = parseFloat(form.saldo_previsao_2026) || 0;

    if (!Number.isInteger(codigo) || codigo <= 0) return setErroForm("Informe um código de projeto válido.");
    if (!form.nome_projeto.trim()) return setErroForm("Informe o nome do projeto.");
    if (valorContrato2025 < 0 || saldoNovosAditivos < 0 || saldoPrevisao2026 < 0) {
      return setErroForm("Valor do Contrato, Saldo Novos/Aditivos e Saldo Previsão não podem ser negativos.");
    }

    const saldoTotal = valorContrato2025 + saldoNovosAditivos;
    const somaMeses = form.meses.reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
    if (somaMeses > saldoTotal + 0.01) {
      return setErroForm(
        `A soma da distribuição mensal (${formatCurrency(somaMeses)}) não pode ultrapassar o Saldo Total do contrato (${formatCurrency(saldoTotal)}).`
      );
    }

    setSalvando(true);
    setErroForm("");
    try {
      const meses = {};
      for (let i = 0; i < 12; i++) meses[i + 1] = parseFloat(form.meses[i]) || 0;
      const payload = {
        codigo,
        setor: form.setor.trim() || null,
        nome_projeto: form.nome_projeto.trim(),
        valor_contrato_2025: valorContrato2025,
        saldo_novos_aditivos: saldoNovosAditivos,
        saldo_previsao_2026: saldoPrevisao2026,
        ano_referencia: ano,
        meses,
      };
      if (editando) await putNovasVendasOrcamento(editando.id, payload);
      else await postNovasVendasOrcamento(payload);
      setModalAberto(false);
      await carregarTudo();
    } catch (e) {
      setErroForm(e.message);
    } finally {
      setSalvando(false);
    }
  }

  async function excluirProjeto(projeto) {
    if (!projeto) return;
    if (!window.confirm(`Excluir o projeto "${projeto.nome_projeto}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await deleteNovasVendasOrcamento(projeto.id);
      await carregarTudo();
    } catch (e) {
      setErro(e.message);
    }
  }

  const saldoTotalForm = (parseFloat(form.valor_contrato_2025) || 0) + (parseFloat(form.saldo_novos_aditivos) || 0);
  const somaMesesForm = form.meses.reduce((acc, v) => acc + (parseFloat(v) || 0), 0);
  const excedeuSaldoTotal = somaMesesForm > saldoTotalForm + 0.01;

  return (
    <div style={{ display: "flex", height: "100vh", overflow: "hidden", background: C.bg, fontFamily: "'DM Sans', sans-serif", color: C.text }}>
      <SideBarNavGestaoOrcamentaria isLight={isLight} />

      <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "auto" }}>
        {/* Header */}
        <header style={{
          background: "linear-gradient(90deg, #4c1d95 0%, #9d174d 100%)",
          color: "#fff", padding: "14px 24px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
          boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 22, fontWeight: 800 }}>YOUX</span>
            <span style={{ fontSize: 18, fontWeight: 300 }}>GROUP</span>
            <span style={{ fontSize: 13, fontWeight: 500, opacity: 0.8, marginLeft: 8 }}>· Novas Vendas</span>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <select
              value={ano}
              onChange={(e) => setAno(parseInt(e.target.value, 10))}
              style={{ background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 10px", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              {OPCOES_ANO.map((a) => <option key={a} value={a} style={{ color: "#0F172A" }}>{a}</option>)}
            </select>
            <button onClick={() => setIsLight((l) => !l)} style={{
              display: "flex", alignItems: "center", gap: 6,
              background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)",
              borderRadius: 8, padding: "7px 12px", cursor: "pointer", color: "#fff", fontSize: 12, fontWeight: 600,
            }}>
              {isLight ? <Sun size={14} /> : <Moon size={14} />}
              {isLight ? "Tema Claro" : "Tema Escuro"}
            </button>
            <button
              onClick={() => navigate("/inicio")}
              style={{ display: "flex", alignItems: "center", gap: 6, background: "rgba(255,255,255,0.12)", border: "1px solid rgba(255,255,255,0.25)", borderRadius: 8, padding: "7px 14px", cursor: "pointer", color: "#fff", fontSize: 13, fontWeight: 600 }}
            >
              Menu Principal
            </button>
          </div>
        </header>

        <main style={{ padding: 24, display: "flex", flexDirection: "column", gap: 24 }}>
          {erro && (
            <div style={{
              display: "flex", alignItems: "center", gap: 8, background: `${C.danger}18`,
              border: `1px solid ${C.danger}`, color: C.danger, borderRadius: 10, padding: "10px 14px", fontSize: 13,
            }}>
              <AlertTriangle size={16} /> {erro}
            </div>
          )}

          {/* KPIs */}
          <section style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            <KpiRow items={kpiItems} C={C} />
          </section>

          {/* Tabela principal */}
          <section>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12, flexWrap: "wrap", gap: 12 }}>
              <h2 style={{ margin: 0, fontSize: 14, fontWeight: 700, color: C.text }}>
                Novas Vendas · {ano}
              </h2>
              <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <button
                  onClick={exportarParaExcel}
                  disabled={loading || projetosOrdenados.length === 0}
                  title="Exportar dados para Excel"
                  style={{ ...btnStyle(C.success, true), opacity: loading || projetosOrdenados.length === 0 ? 0.45 : 1, cursor: loading || projetosOrdenados.length === 0 ? "not-allowed" : "pointer" }}
                >
                  <FileSpreadsheet size={15} /> Exportar Excel
                </button>
                <button onClick={() => abrirModal(null)} style={btnStyle(C.brand)}>
                  <Plus size={15} /> Novo projeto
                </button>
              </div>
            </div>

            {loading ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13 }}>Carregando…</div>
            ) : projetosOrdenados.length === 0 ? (
              <div style={{ padding: 48, textAlign: "center", color: C.muted, fontSize: 13, background: C.card, border: `1px solid ${C.border}`, borderRadius: 14 }}>
                Nenhum projeto cadastrado para {ano}. Clique em "Novo projeto" para começar.
              </div>
            ) : (
              <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 14, overflow: "hidden" }}>
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 1700 }}>
                    <thead>
                      <tr>
                        <th style={thStyle(C)}>Código</th>
                        <th style={thStyle(C)}>Setor</th>
                        <th style={{ ...thStyle(C), textAlign: "left" }}>Nome do Projeto</th>
                        <th style={thStyle(C)}>Valor do Contrato em 31.12.2025</th>
                        <th style={thStyle(C)}>Saldo Novos/Aditivos 2027</th>
                        <th style={thStyle(C)}>Saldo Total</th>
                        <th style={thStyle(C)}>Saldo Previsão 31.12.2026</th>
                        {MESES_ABREV.map((m) => <th key={m} style={thStyle(C)}>{m}</th>)}
                        <th style={thStyle(C)}>Total</th>
                        <th style={thStyle(C)}>Ações</th>
                      </tr>
                    </thead>
                    <tbody>
                      {projetosOrdenados.map((p) => {
                        const total = totalProjeto(p);
                        return (
                          <tr key={p.id}>
                            <td style={tdStyle(C)}>{p.codigo}</td>
                            <td style={tdStyle(C)}>{p.setor || "—"}</td>
                            <td style={{ ...tdStyle(C), textAlign: "left" }}>{p.nome_projeto}</td>
                            <td style={tdStyle(C)}>{formatCurrency(p.valor_contrato_2025)}</td>
                            <td style={tdStyle(C)}>{formatCurrency(p.saldo_novos_aditivos)}</td>
                            <td style={tdStyle(C)}>{formatCurrency(p.saldo_total)}</td>
                            <td style={tdStyle(C)}>{formatCurrency(p.saldo_previsao_2026)}</td>
                            {MESES_ABREV.map((_, i) => {
                              const v = Number(p.meses?.[i + 1] || 0);
                              return <td key={i} style={tdStyle(C)}>{formatCurrency(v)}</td>;
                            })}
                            <td style={{ ...tdStyle(C), fontWeight: 700 }}>{formatCurrency(total)}</td>
                            <td style={tdStyle(C)}>
                              <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
                                <IconBtn title="Editar projeto" onClick={() => abrirModal(p)} C={C}><Pencil size={13} /></IconBtn>
                                <IconBtn title="Excluir projeto" onClick={() => excluirProjeto(p)} C={C} danger><Trash2 size={13} /></IconBtn>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr style={{ fontWeight: 800, borderTop: `2px solid ${C.border}` }}>
                        <td style={{ ...tdStyle(C, true), textAlign: "left" }} colSpan={7}>Total Geral</td>
                        {totaisPorMes.map((v, i) => (
                          <td key={i} style={tdStyle(C, true)}>{formatCurrency(v)}</td>
                        ))}
                        <td style={tdStyle(C, true)}>{formatCurrency(totalGeral)}</td>
                        <td style={tdStyle(C)} />
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </section>
        </main>
      </div>

      {/* Modal: Novo/Editar Projeto */}
      {modalAberto && (
        <ModalShell C={C} title={editando ? "Editar Projeto" : "Novo Projeto"} onClose={() => setModalAberto(false)} maxWidth={800}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Ano de referência: <strong style={{ color: C.text }}>{ano}</strong></p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
              <Campo label="Código" C={C}>
                <input style={inputStyle(C)} type="number" step="1" value={form.codigo}
                  onChange={(e) => setForm({ ...form, codigo: e.target.value })} />
              </Campo>
              <Campo label="Setor" C={C}>
                <input style={inputStyle(C)} type="text" placeholder="Ex: 3. Setor" value={form.setor}
                  onChange={(e) => setForm({ ...form, setor: e.target.value })} />
              </Campo>
            </div>

            <Campo label="Nome do Projeto" C={C}>
              <input style={inputStyle(C)} type="text" value={form.nome_projeto}
                onChange={(e) => setForm({ ...form, nome_projeto: e.target.value })} />
            </Campo>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
              <Campo label="Valor do Contrato em 31.12.2025" C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="0.01" value={form.valor_contrato_2025}
                  onChange={(e) => setForm({ ...form, valor_contrato_2025: e.target.value })} />
              </Campo>
              <Campo label="Saldo Contrato Novos/Aditivos 2026" C={C}>
                <input style={inputStyle(C)} type="number" min="0" step="0.01" value={form.saldo_novos_aditivos}
                  onChange={(e) => setForm({ ...form, saldo_novos_aditivos: e.target.value })} />
              </Campo>
              <Campo label="Saldo Total" C={C}>
                <div style={{ ...inputStyle(C), background: C.bgActive, fontWeight: 700, textAlign: "right" }}>
                  {formatCurrency(saldoTotalForm)}
                </div>
              </Campo>
            </div>

            <Campo label="Saldo Contrato Previsão 31.12.2026" C={C}>
              <input style={inputStyle(C)} type="number" min="0" step="0.01" value={form.saldo_previsao_2026}
                onChange={(e) => setForm({ ...form, saldo_previsao_2026: e.target.value })} />
            </Campo>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 8, flexWrap: "wrap", gap: 8 }}>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em" }}>
                  Distribuição mensal
                </p>
                <p style={{ margin: 0, fontSize: 11, fontWeight: 700, color: excedeuSaldoTotal ? C.danger : C.muted }}>
                  Distribuído: {formatCurrency(somaMesesForm)} / Saldo Total: {formatCurrency(saldoTotalForm)}
                </p>
              </div>
              <MesesGrid
                C={C}
                valores={form.meses}
                onChange={(i, v) => {
                  const novo = [...form.meses];
                  novo[i] = v;
                  setForm({ ...form, meses: novo });
                }}
              />
              {excedeuSaldoTotal && (
                <p style={{ margin: "6px 0 0", fontSize: 11, color: C.danger }}>
                  A distribuição mensal ultrapassa o Saldo Total do contrato.
                </p>
              )}
            </div>

            {erroForm && <p style={{ color: C.danger, fontSize: 12, margin: 0 }}>{erroForm}</p>}
          </div>
          <ModalFooter
            C={C} salvando={salvando}
            onCancel={() => setModalAberto(false)}
            onSave={salvarProjeto}
            labelSalvar={editando ? "Atualizar" : "Salvar projeto"}
          />
        </ModalShell>
      )}
    </div>
  );
}

// ── Helpers de UI locais ─────────────────────────────────────────────────────

function MesesGrid({ C, valores, onChange }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 }}>
      {MESES_ABREV.map((m, i) => (
        <Campo key={m} label={m} C={C}>
          <input style={inputStyle(C)} type="number" step="0.01" value={valores[i]}
            onChange={(e) => onChange(i, e.target.value)} />
        </Campo>
      ))}
    </div>
  );
}

function Campo({ label, children, C }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted }}>
        {label}
      </label>
      {children}
    </div>
  );
}

function inputStyle(C) {
  return {
    background: C.bg, border: `1px solid ${C.border}`, borderRadius: 8,
    padding: "9px 12px", fontSize: 13, color: C.text, fontFamily: "inherit", outline: "none",
  };
}

function btnStyle(color, secondary = false) {
  return {
    display: "flex", alignItems: "center", gap: 6,
    background: secondary ? "transparent" : color,
    border: secondary ? `1px solid ${color}` : "none",
    color: secondary ? color : "#fff",
    borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 700,
    fontFamily: "inherit", cursor: "pointer",
  };
}

function thStyle(C) {
  return {
    padding: "8px 10px", fontSize: 11, fontWeight: 700, color: C.muted,
    textTransform: "uppercase", letterSpacing: "0.04em", textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
  };
}

function tdStyle(C, strong = false) {
  return {
    padding: "8px 10px", fontSize: 12, color: C.text, textAlign: "right",
    borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap",
    fontWeight: strong ? 700 : 400,
  };
}

function IconBtn({ children, onClick, title, C, danger = false }) {
  return (
    <button
      onClick={onClick}
      title={title}
      style={{
        display: "flex", alignItems: "center", justifyContent: "center",
        width: 26, height: 26, borderRadius: 6, border: `1px solid ${C.border}`,
        background: "transparent", color: danger ? C.danger : C.text, cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}

function ModalShell({ title, children, onClose, C, maxWidth = 480 }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 50, padding: 16 }}>
      <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 16, padding: 24, width: "100%", maxWidth, boxShadow: "0 24px 64px rgba(0,0,0,0.4)", maxHeight: "90vh", overflowY: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <p style={{ margin: 0, fontSize: 16, fontWeight: 700, color: C.text }}>{title}</p>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", color: C.muted, display: "flex" }}>
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function ModalFooter({ C, salvando, onCancel, onSave, labelSalvar }) {
  return (
    <div style={{ display: "flex", gap: 10, marginTop: 20, position: "sticky", bottom: 0, background: C.card, paddingTop: 10 }}>
      <button style={{ flex: 1, background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 0", color: C.text, fontSize: 13, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}
        onClick={onCancel}>
        Cancelar
      </button>
      <button style={{ flex: 1, background: C.brand, border: "none", borderRadius: 8, padding: "10px 0", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit", opacity: salvando ? 0.6 : 1 }}
        onClick={onSave} disabled={salvando}>
        {salvando ? "Salvando..." : labelSalvar}
      </button>
    </div>
  );
}
